/**
 * StaticDocument.tsx — renders a GridPlan with pure CSS grid/subgrid and zero
 * measurement. Content + gutter share row tracks, so the browser keeps every
 * comment bubble aligned to its anchor and grows the row to fit a long comment.
 *
 * This is the static replacement for the JS-measured layout in
 * HtmlWithAnchors.tsx. It is a scaffold: not yet wired into the app.
 */

import React from "react";
import { Box, IconButton, SxProps, Theme, Tooltip } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UndoIcon from "@mui/icons-material/Undo";
import { Localized } from "@fluent/react";
import { katexnize } from "../../../../app/latexTools";
import { noteIcon } from "../components/noteIcon";
import { HastNode, nodeText, toHtml } from "./hast";
import { GridPlan, GridRow, GutterCell, PlacedBlock } from "./segmenter";
import { COL_HIDDEN_ATTR, HiddenIndexMap } from "./tableVisibility";

type DeptLookup = Record<number, { code: string; color?: string }>;
type DocLinkLookup = Record<number, { code: string; color?: string }>;

interface Props {
    plan: GridPlan;
    gutterWidth?: number;
    deptGutterWidth?: number;
    docLinkGutterWidth?: number;
    /** Width of the trailing AI-suggestion column. 0 (the default) keeps the
     *  track in the template but collapses it — the assess view passes nothing. */
    aiGutterWidth?: number;
    departments?: DeptLookup;
    documentTypes?: DocLinkLookup;
    /** Selected units. Multi-select is driven by the container (ctrl/meta-click);
     *  single-select callers (assess) pass a one-element set. */
    selectedKeys?: Set<string> | null;
    /** `multi` is true when the click carried ctrl/meta — the container decides
     *  whether to extend the selection or replace it. */
    onSelect?: (key: string, multi?: boolean) => void;
    /** Attached to the root grid so the container can query content for search / scroll. */
    gridRef?: React.Ref<HTMLDivElement>;
    /** When set, comment bubbles whose key isn't in the set are dimmed (filter active). */
    filteredKeys?: Set<string> | null;
    /** Keys to render de-emphasised (assess mode): sub-blocks inside a visible
     *  block whose department isn't selected. Dims content + the dept badge. */
    dimmedKeys?: Set<string> | null;
    /** Indices into `plan.content` to hide entirely (whole row: content + gutter)
     *  — used by search to show only matching top-level blocks. */
    hiddenBlocks?: Set<number> | null;
    /** Assess mode: per-blockKey department comment rendered in the (widened)
     *  dept column next to the department badge. `comment`/`authorFullName` are
     *  the latest message; `count` is the thread size. When provided, the dept
     *  column shows badge + comment box; when omitted, today's badge-only chip. */
    deptComments?: Record<string, { comment: string; authorFullName?: string; count?: number }> | null;
    /** Per-blockKey author of the saved review comment, shown atop the yellow
     *  gutter bubble. Keys without an entry render the bubble text only. */
    commentAuthors?: Record<string, string> | null;
    /** Per-block AI suggestion counts, keyed by LOWERCASED block key (the API's
     *  targetBlockKey casing does not match the document's — see
     *  selectImportedDocumentSuggestionsForBlock). Rendered as a badge in the
     *  trailing column so the reviewer sees where suggestions exist without
     *  selecting each block in turn. */
    suggestionCounts?: Record<string, number> | null;
    /** A plain click on an AI badge selects its block and then calls this, so
     *  the container can open the suggestions dialog for it. */
    onOpenSuggestions?: (key: string) => void;
    /** Debug overlay: outlines the layout structure (see DEBUG_GRID_SX). */
    debugGrid?: boolean;
    /** Draw a hairline across each annotated row band, tying the block to its
     *  gutter bubble (see RowConnector). */
    rowConnectors?: boolean;
    /** Advanced mode. Omitted (or undefined) means the document renders exactly
     *  as it always has — no toolbars, no extra grid tracks. Must be
     *  referentially stable: ContentBlock is memoized on it. */
    advanced?: AdvancedControls;
}

/**
 * Everything the advanced ("delete parts of the document") mode needs.
 *
 * The hidden-* collections drive which control each part shows (delete vs
 * restore); the DIMMING of a hidden table cell comes from `data-col-hidden`,
 * set on the cells by the AST transform, while a hidden BLOCK is dimmed here by
 * id — a block needs no AST surgery, the renderer can simply skip or fade its
 * whole grid band.
 */
export interface AdvancedControls {
    /** 0-based hidden column indices per table id. */
    hiddenColumns?: HiddenIndexMap;
    /** Ids of top-level blocks marked hidden (see PlacedBlock.blockId). */
    hiddenBlockIds?: Set<string>;
    /**
     * Tables the AST transform refuses to edit because their source spans
     * overlap (~2.5% of the real corpus). Their controls render disabled rather
     * than silently doing nothing.
     */
    refusedTables?: Set<string>;
    onToggleColumn?: (tableId: string, columnIndex: number) => void;
    onToggleBlock?: (blockId: string) => void;
}

/** Width of the master grid's leading block-control column. */
const TOOLBAR_TRACK = 30;

/**
 * The visual link between a block and its comment. Block and bubble are siblings
 * in the SAME grid row, so their top edges already align exactly — the problem is
 * only that the eye can't track across the whitespace between them. A hairline
 * spanning the row band across ALL columns (content → comment gutter → dept →
 * doc-link) IS the connector: no measurement, no anchor geometry, just a ruled
 * line like an index or a ledger. It runs the full width so the eye can track
 * from the document text all the way to the side stamps, not just the comment.
 *
 * Drawn at the band's top edge, which is where the block starts. The bubble
 * itself sits lower, on the block's first text line (see FIRST_LINE_SX).
 */
const RowConnector: React.FC<{ row: number }> = ({ row }) => (
    <Box
        aria-hidden
        data-row-connector=""
        style={{ gridRow: row, gridColumn: "1 / -1", pointerEvents: "none", alignSelf: "start" }}
        sx={{ borderTop: "1px dashed", borderColor: "rgba(0, 0, 0, 0.18)", width: "100%" }}
    />
);

// Debug overlay — makes the segmentation visible when the gutter looks wrong.
// Each structural layer gets its own visual channel so they can nest without
// fighting: a top-level table is BOTH a grid-row block and a subgrid container,
// and shows both marks.
//   blue outline   — top-level block: one band of the master grid
//   purple inset   — subgrid container: table / list whose children own the rows
//   green outline   — annotatable unit: carries a data-block-key
export const DEBUG_GRID_COLORS = {
    topLevel: "rgba(2, 136, 209, 0.7)",
    subgrid: "rgba(156, 39, 176, 0.7)",
    blockKey: "rgba(46, 125, 50, 0.7)",
} as const;

const DEBUG_GRID_SX: SxProps<Theme> = {
    "& [data-doc-content]": {
        outline: `1px solid ${DEBUG_GRID_COLORS.topLevel}`,
        outlineOffset: "4px",
    },
    "& [data-subgrid]": {
        boxShadow: `inset 0 0 0 2px ${DEBUG_GRID_COLORS.subgrid}`,
    },
    // Excluded above: a top-level wrapper / subgrid container usually carries a
    // key too, and it already has its own mark — don't stack a third outline.
    "& [data-block-key]:not([data-doc-content]):not([data-subgrid])": {
        outline: `1px dashed ${DEBUG_GRID_COLORS.blockKey}`,
        outlineOffset: "2px",
    },
};

function blockKeyOf(node: HastNode): string | undefined {
    return node.type === "element" ? node.properties["data-block-key"] : undefined;
}

/** Marks a top-level block the reviewer has marked for deletion (advanced mode). */
const BLOCK_HIDDEN_ATTR = "data-block-hidden";

/** Everything marked for deletion, whichever kind it is — one dim rule covers all. */
const HIDDEN_PART_SELECTOR = `& [${COL_HIDDEN_ATTR}], & [${BLOCK_HIDDEN_ATTR}]`;

function containsTag(node: HastNode, tag: string): boolean {
    if (node.type !== "element") return false;
    return node.tagName === tag || node.children.some((c) => containsTag(c, tag));
}

/**
 * Short description of a top-level block, for its delete control's tooltip.
 * Its own text where it has any, otherwise what kind of thing it is — an image
 * or a rule has no text but still needs to be identifiable in the tooltip.
 */
function blockLabel(block: PlacedBlock, maxLength = 60): string {
    const text = nodeText(block.node).replace(/\s+/g, " ").trim();
    if (text) return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
    if (block.node.type === "element") {
        if (block.node.tagName === "table") return "table";
        if (containsTag(block.node, "img")) return "image";
        return block.node.tagName;
    }
    return "block";
}

// Elements that are themselves the grid item — render their inner content only.
const STRUCTURAL = new Set(["td", "th", "li"]);

// Indent per list-nesting level (see PlacedBlock.depth).
const INDENT_EM = 1.5;

// Emulates the old view's collapsed 1px table borders (importedDocument.css):
// container draws top + left, each cell draws right + bottom → no doubling.
const TABLE_BORDER = "1px solid #e1e1e1";
const TH_BG = "#e1e1e1";

function leafHtml(node: HastNode): string {
    if (node.type !== "element") return toHtml([node]);
    const inner = STRUCTURAL.has(node.tagName) ? toHtml(node.children) : toHtml([node]);
    return katexnize(inner);
}

/** Per-cell styling that mirrors `& td, & th` / `& th` from the old view. */
function cellSx(node: HastNode): SxProps<Theme> | undefined {
    if (node.type !== "element" || (node.tagName !== "td" && node.tagName !== "th")) {
        return undefined;
    }
    const base = {
        borderRight: TABLE_BORDER,
        borderBottom: TABLE_BORDER,
        padding: "6px 8px",
        boxSizing: "border-box",
        minWidth: "1em",
        verticalAlign: "top",
    } as const;
    return node.tagName === "th"
        ? { ...base, bgcolor: TH_BG, fontWeight: "bold", textAlign: "left" }
        : base;
}

/**
 * Gutter items line up with their fragment's FIRST TEXT LINE, not the row's top
 * edge. A heading's top margin (3.5rem for h1 under `.imported-document`), a
 * paragraph's 1em, or a Word inline margin all sit INSIDE the content cell (a
 * grid item doesn't collapse margins out), so a top-aligned badge floated up to
 * 56px above the heading it belongs to. Baseline alignment fixes it for any
 * margin source with no measurement: top-level leaf blocks and every gutter item
 * (`alignSelf: "baseline"` inline) join the row's baseline group.
 *
 * Excluded:
 *  - subgrid containers (tables, lists) span many rows; shifting one would break
 *    the subgrid. Their cells stretch to draw borders, so table/list rows stay
 *    top-aligned as before.
 *  - blocks with an image: their baseline is the image's BOTTOM, which would drop
 *    the badge to the foot of a tall diagram.
 */
const FIRST_LINE_SX = {
    "& > [data-doc-content]:not([data-subgrid])": { alignSelf: "baseline" },
    "& > [data-doc-content]:not([data-subgrid]):has(img)": { alignSelf: "start" },
} as const;

// Mirrors `.comment-selected` from the classic view (HtmlWithAnchors).
const SELECTED_SX = {
    position: "relative",
    boxShadow: "inset 0 0 0 3px #2e7d32, 0 0 0 2px rgba(46, 125, 50, 0.35)",
    backgroundColor: "rgba(46, 125, 50, 0.06)",
    borderRadius: 1,
} as const;

// Mirrors `.comment-hover` from the classic view: cursor feedback telling you
// which unit a click would select.
export const HOVER_TINT = "rgba(25, 118, 210, 0.06)";

/**
 * One content block, placed in its enclosing grid (master grid or a subgrid).
 *
 * Memoized: hover state lives in StaticDocument, so every block the cursor
 * crosses re-renders the grid. The highlight is a CSS rule on the root — no
 * block's own props change — so without memo we'd re-parse every block's
 * innerHTML on each mouse-over. `block` comes from the plan and `selectedKeys`
 * is a memoized Set, so the default shallow compare holds.
 */
const ContentBlock: React.FC<{
    block: PlacedBlock;
    top?: boolean;
    selectedKeys?: Set<string> | null;
    advanced?: AdvancedControls;
    /** Grid columns to shift right by, for a leading toolbar track the plan
     *  knows nothing about (the master block column, or a table's row band). */
    colOffset?: number;
}> = React.memo(({
    block,
    top,
    selectedKeys,
    advanced,
    colOffset = 0,
}) => {
    const style: React.CSSProperties = {
        gridRow: `${block.rowStart} / span ${block.rowSpan}`,
        gridColumn: `${block.colStart + colOffset} / span ${block.colSpan}`,
        minWidth: 0,
        // A nested list item is lifted out of its parent <li> into a row of its
        // own (that is the only way it can have a gutter cell), which flattens the
        // markup. Indent it back to where the reader expects it.
        ...(block.depth ? { paddingLeft: `${block.depth * INDENT_EM}em` } : null),
    };

    // data-block-key makes every unit addressable (click-to-select / scroll /
    // highlight); data-doc-content marks top-level wrappers so search targets
    // document text only and skips the gutter (both are grid siblings).
    //
    // The wrapper only carries the key when the node's own tag is NOT preserved
    // in the rendered HTML — i.e. structural cells (td/th/li render inner HTML)
    // and subgrid containers (table/list render children, not the tag). A plain
    // leaf (e.g. <p data-block-key>) keeps its key via outerHTML, so adding it to
    // the wrapper too would double it. A keyless cell falls back to its row key
    // so a row-level annotation is still selectable from any cell.
    const ownKey = blockKeyOf(block.node);
    const isStructural = block.node.type === "element" && STRUCTURAL.has(block.node.tagName);
    const tagPreserved = !block.subgrid && !isStructural;
    const wrapperKey = tagPreserved ? block.rowKey : ownKey ?? block.rowKey;
    // A cell that carries ONLY the row key (no key of its own) is part of a
    // multi-cell row. Mark it so the root highlight rule skips it — selecting a
    // row paints one band overlay instead of a ring around every cell.
    const carriesRowKey = block.rowKey != null && ownKey == null;
    const dataAttrs: Record<string, string> = {};
    if (wrapperKey) dataAttrs["data-block-key"] = wrapperKey;
    if (carriesRowKey) dataAttrs["data-row-cell"] = "";
    // A td/th renders as inner HTML (see STRUCTURAL), so the cell's own
    // attributes never reach the DOM — the transform's hidden-column marker has
    // to be re-applied to the wrapper or the dim rule would match nothing.
    if (block.node.type === "element" && block.node.properties[COL_HIDDEN_ATTR] != null) {
        dataAttrs[COL_HIDDEN_ATTR] = "";
    }
    // Mark subgrid containers (table / owner cell / list) so the dim rule skips
    // them — dimming a container would composite onto matching descendants.
    if (block.subgrid) dataAttrs["data-subgrid"] = "";
    if (top) dataAttrs["data-doc-content"] = "";
    // Advanced mode: a block marked for deletion is dimmed whole (its own text,
    // and for a table its cells and inner controls too) rather than removed.
    if (top && block.blockId && advanced?.hiddenBlockIds?.has(block.blockId)) {
        dataAttrs[BLOCK_HIDDEN_ATTR] = "";
    }
    // Source backend element id on the grid-row wrapper — for verifying the
    // element→DOM mapping. Top-level only (set by the segmenter on top blocks).
    if (top && block.elementId != null) dataAttrs["data-element-id"] = block.elementId;

    if (block.subgrid) {
        const isTable = block.node.type === "element" && block.node.tagName === "table";
        // When a whole row is selected, every cell shares its key. Rather than
        // ring each cell, draw a single overlay spanning the row's band + all
        // columns. With multi-select several rows of one table can be selected at
        // once, so this is a filter — one overlay per selected row, deduped by row
        // key (every cell of the row carries it).
        //
        // The overlay's HEIGHT comes from `rowBand`, not from the representative
        // cell's `rowSpan`: where a row starts a rowspan, its first cell spans
        // that row AND the ones below, so sizing the overlay from it painted the
        // selection across the rest of the table.
        const selectedRows =
            isTable && selectedKeys?.size
                ? block.subgrid.children.filter(
                      (c, i, all) =>
                          c.rowKey != null &&
                          selectedKeys.has(c.rowKey) &&
                          all.findIndex((o) => o.rowKey === c.rowKey) === i,
                  )
                : [];
        // Prefer content-proportional widths derived from PDF `data-bbox`
        // (weighted fr — fills 100% width, min-content floor keeps wrapping).
        // Fall back to CSS intrinsic sizing when bbox data is absent/unusable.
        const tableCols = block.subgrid.colFractions
            ? block.subgrid.colFractions.map((f) => `minmax(min-content, ${f}fr)`).join(" ")
            : `repeat(${block.subgrid.cols}, minmax(min-content, auto))`;
        // The column controls sit on the table's own column tracks (subgrid row 1,
        // reserved by the segmenter), so the table needs no extra track and
        // `colFractions` stays a pure description of the document.
        const toolbar = block.subgrid.toolbar;
        return (
            <Box
                {...dataAttrs}
                style={style}
                sx={{
                    display: "grid",
                    gridTemplateRows: "subgrid",
                    // Column sizing (see `tableCols` above):
                    //  - With PDF `data-bbox`: weighted `fr` units reproduce the
                    //    source column proportions, fill 100% width, and keep a
                    //    min-content floor so cells wrap instead of overflowing.
                    //  - Fallback (`auto`): grid grows each track to max-content
                    //    (preserving content proportions) then stretches to fill
                    //    100% — mirrors classic's native `table-layout:auto`.
                    //    RESIDUAL DIFF: leftover slack is split EQUALLY across auto
                    //    tracks vs. native's content-proportional split; close
                    //    enough, the content-sized base dominates. (`1fr` here made
                    //    every column equal-width — the bug this replaced.)
                    gridTemplateColumns: isTable ? tableCols : "1fr",
                    ...(isTable && { borderTop: TABLE_BORDER, borderLeft: TABLE_BORDER }),
                    ...cellSx(block.node),
                }}
            >
                {toolbar && (
                    <ColumnToolbar
                        toolbar={toolbar}
                        cols={block.subgrid.cols}
                        advanced={advanced}
                    />
                )}
                {block.subgrid.children.map((child, i) => (
                    <ContentBlock key={i} block={child} selectedKeys={selectedKeys} />
                ))}
                {selectedRows.map((selectedRow) => (
                    <Box
                        key={`rh-${selectedRow.rowKey}`}
                        data-row-highlight=""
                        aria-hidden
                        style={{
                            gridRow: `${selectedRow.rowStart} / span ${selectedRow.rowBand ?? selectedRow.rowSpan}`,
                            gridColumn: "1 / -1",
                            pointerEvents: "none",
                        }}
                        sx={SELECTED_SX}
                    />
                ))}
            </Box>
        );
    }

    return (
        <Box
            {...dataAttrs}
            style={style}
            sx={cellSx(block.node)}
            dangerouslySetInnerHTML={{ __html: leafHtml(block.node) }}
        />
    );
});

/**
 * A single delete/restore control. Rendered on the grid track of the part it
 * acts on, so it lines up with that part for free — no measurement, no anchor
 * geometry. That alignment is the whole reason the toolbar bands are real grid
 * tracks rather than strips drawn around the table.
 */
const PartControl: React.FC<{
    hidden: boolean;
    /** The table's markup can't be edited safely — explain instead of no-op. */
    blocked?: boolean;
    label: string;
    onToggle: () => void;
    style: React.CSSProperties;
    /** Block controls span a whole band — pin them to its top edge, next to the
     *  first line of the block rather than floating in the middle of it. */
    alignTop?: boolean;
}> = ({ hidden, blocked, label, onToggle, style, alignTop }) => {
    const title = blocked ? (
        <Localized id="imported-doc-part-blocked">
            <span>
                This table's rows and columns overlap, so its columns cannot be hidden
                without breaking the layout.
            </span>
        </Localized>
    ) : hidden ? (
        <Localized id="imported-doc-part-restore" vars={{ part: label }}>
            <span>{`Restore "${label}"`}</span>
        </Localized>
    ) : (
        <Localized id="imported-doc-part-delete" vars={{ part: label }}>
            <span>{`Delete "${label}"`}</span>
        </Localized>
    );

    return (
        <Box
            data-part-toolbar=""
            style={style}
            sx={{
                display: "flex",
                alignItems: alignTop ? "flex-start" : "center",
                justifyContent: "center",
                // Fill the band the control was placed on, so the strip is
                // exactly as tall as the block it deletes. The master grid is
                // `align-items: start`, which would otherwise shrink it to the
                // icon and leave a stub floating beside a tall block.
                alignSelf: "stretch",
                borderRight: TABLE_BORDER,
                borderBottom: TABLE_BORDER,
                bgcolor: hidden ? "var(--yellow-100)" : "action.hover",
                minWidth: 0,
            }}
        >
            <Tooltip title={title}>
                {/* Wrapper: a disabled IconButton fires no events, so Tooltip
                    would never show the explanation. */}
                <Box component="span" sx={{ display: "inline-flex" }}>
                    <IconButton
                        size="small"
                        disabled={blocked}
                        color={hidden ? "warning" : "default"}
                        onClick={(e) => {
                            // The grid's own click handler resolves the nearest
                            // annotated unit — without this the press would also
                            // select the row the control sits on.
                            e.stopPropagation();
                            onToggle();
                        }}
                        sx={{ p: 0.25 }}
                    >
                        {hidden ? <UndoIcon fontSize="small" /> : <DeleteOutlineIcon fontSize="small" />}
                    </IconButton>
                </Box>
            </Tooltip>
        </Box>
    );
};

type TableToolbar = NonNullable<NonNullable<PlacedBlock["subgrid"]>["toolbar"]>;

/**
 * Advanced mode: a table's column control band — one control per column across
 * the top, on subgrid row 1 (reserved by the segmenter). Each control sits on
 * its own column's track, so it lines up with the column it deletes for free.
 */
const ColumnToolbar: React.FC<{
    toolbar: TableToolbar;
    cols: number;
    advanced?: AdvancedControls;
}> = ({ toolbar, cols, advanced }) => {
    const { tableId } = toolbar;
    const hiddenColumns = advanced?.hiddenColumns;
    const hiddenCols = React.useMemo(
        () => new Set(hiddenColumns?.[tableId] ?? []),
        [hiddenColumns, tableId],
    );
    const blocked = advanced?.refusedTables?.has(tableId);
    if (!advanced?.onToggleColumn) return null;

    return (
        <>
            {Array.from({ length: cols }, (_, c) => (
                <PartControl
                    key={`tc${c}`}
                    hidden={hiddenCols.has(c)}
                    blocked={blocked}
                    // ~12.8% of real columns have no header text, so the
                    // position is the fallback label rather than a blank one.
                    label={toolbar.columnLabels[c] || `Column ${c + 1}`}
                    onToggle={() => advanced.onToggleColumn!(toolbar.tableId, c)}
                    style={{ gridRow: 1, gridColumn: c + 1 }}
                />
            ))}
        </>
    );
};

/**
 * Gives a multi-part gutter cell (badge + comment box) the SAME baseline as a
 * lone 28px chip, whatever it contains. The cell's items are top-aligned
 * (`flex-start`), so none takes part in flex baseline alignment and the cell's
 * baseline is its first item's — this strut: a zero-width line exactly as tall
 * and in the same font size as SideChip, so its baseline sits where a chip's
 * centred code does. Without it the cell inherited the comment's first text line
 * (the author name, just under the box's top edge) and the comment box sat a few
 * pixels below the badges next to it. See FIRST_LINE_SX.
 */
const BaselineStrut: React.FC = () => (
    // mr cancels the parent's flex gap, so the strut takes no horizontal space.
    <Box aria-hidden sx={{ width: 0, mr: -0.5, height: 28, lineHeight: "28px", fontSize: 10, flexShrink: 0 }}>
        {"\u200b"}
    </Box>
);

/** One gutter cell (comment bubble or collapsed aggregate). */
const GutterCellView: React.FC<{
    row: GridRow;
    col: number;
    selectedKeys?: Set<string> | null;
    onSelect?: (key: string, multi?: boolean) => void;
    filteredKeys?: Set<string> | null;
    commentAuthors?: Record<string, string> | null;
}> = ({ row, col, selectedKeys, onSelect, filteredKeys, commentAuthors }) => {
    const cell: GutterCell = row.gutter;
    if (cell.kind === "none") return null;

    const baseStyle: React.CSSProperties = { gridRow: row.row, gridColumn: col, alignSelf: "baseline" };

    if (cell.kind === "aggregate") {
        const dimmed = !!filteredKeys && !cell.blockKeys.some((k) => filteredKeys.has(k));
        return (
            <Box
                style={baseStyle}
                onClick={(e) => onSelect?.(cell.blockKeys[0], e.ctrlKey || e.metaKey)}
                sx={{
                    m: 0.25, px: 0.75, py: 0.25,
                    border: "1px dashed", borderColor: "var(--gray-400)", borderRadius: 1,
                    fontSize: ".7rem", color: "text.secondary", cursor: "pointer",
                    opacity: dimmed ? 0.3 : 1,
                }}
            >
                ▾ {cell.count} comments
            </Box>
        );
    }

    const selected = !!selectedKeys?.has(cell.blockKey);
    const dimmed = !!filteredKeys && !filteredKeys.has(cell.blockKey);
    // Notes and comments are distinct visual components (matches the classic
    // view): the note is a circular icon badge, the comment a separate text
    // bubble next to it. Either may be present on its own.
    return (
        <Box
            style={baseStyle}
            sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, opacity: dimmed ? 0.3 : 1 }}
        >
            <BaselineStrut />
            {cell.note && (
                <IconButton
                    size="small"
                    onClick={(e) => onSelect?.(cell.blockKey, e.ctrlKey || e.metaKey)}
                    sx={{
                        bgcolor: "background.paper",
                        boxShadow: 1,
                        flexShrink: 0,
                        "&:hover": { bgcolor: "background.paper" },
                    }}
                >
                    {noteIcon(cell.note)}
                </IconButton>
            )}
            {cell.comment && (
                <Box
                    onClick={(e) => onSelect?.(cell.blockKey, e.ctrlKey || e.metaKey)}
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        px: 0.5, py: 0.25,
                        borderRadius: 1,
                        border: selected ? "1px solid var(--gray-600)" : "1px solid var(--gray-400)",
                        bgcolor: selected ? "var(--yellow-300)" : "var(--yellow-200)",
                        fontSize: ".75rem",
                        cursor: "pointer",
                    }}
                >
                    {commentAuthors?.[cell.blockKey] && (
                        <Box sx={{ fontWeight: 700, fontSize: ".65rem", lineHeight: 1.2 }}>{commentAuthors[cell.blockKey]}</Box>
                    )}
                    {cell.comment}
                </Box>
            )}
        </Box>
    );
};

const SideChip: React.FC<{ row: number; col: number; code: string; color?: string; dimmed?: boolean; onClick?: (e: React.MouseEvent) => void }> = ({
    row, col, code, color, dimmed, onClick,
}) => (
    <Box
        style={{ gridRow: row, gridColumn: col, alignSelf: "baseline" }}
        onClick={onClick}
        sx={{
            mx: 0.5, width: 28, height: 28, borderRadius: 0.75, opacity: dimmed ? 0.35 : 1,
            bgcolor: color || "primary.main", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 10, fontWeight: 700, cursor: "pointer",
        }}
    >
        {code}
    </Box>
);

/**
 * AI-suggestion badge for one row. Outlined and blue (info.main, the same accent
 * ReviewToolbar uses for an active filter): lighter than the solid dept /
 * doc-type code stamps, which are assessment state and keep the stronger voice,
 * and distinct from the note codes' green/red so a suggestion never reads as a
 * verdict. Note this diverges from NotePanel's green `AI (n)` button.
 *
 * A plain click selects the block and opens its suggestions dialog
 * (onOpenSuggestions). A ctrl/meta-click only extends the selection, same as
 * SideChip — opening a dialog for one block of a multi-selection would be
 * ambiguous.
 */
const AiSuggestionChip: React.FC<{
    row: number;
    col: number;
    count: number;
    onClick?: (e: React.MouseEvent) => void;
}> = ({ row, col, count, onClick }) => (
    <Tooltip
        title={
            <Localized id="imported-doc-ai-suggestions" vars={{ count }}>
                <span>{`${count} AI suggestions`}</span>
            </Localized>
        }
    >
        <Box
            data-ai-suggestions=""
            style={{ gridRow: row, gridColumn: col, alignSelf: "baseline" }}
            onClick={onClick}
            sx={{
                mx: 0.5, minWidth: 28, height: 28, px: 0.5, borderRadius: 0.75,
                border: "1px solid", borderColor: "info.main",
                bgcolor: "background.paper", color: "info.main",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 0.25,
                fontSize: 11, fontWeight: 700, cursor: "pointer",
                "&:hover": { bgcolor: "info.main", color: "#fff" },
            }}
        >
            <AutoAwesomeIcon sx={{ fontSize: 13 }} />
            {count}
        </Box>
    </Tooltip>
);

/** Assess-mode dept cell: the department badge next to the assessor's comment
 *  box (with author name) in the widened dept column. Uses a BLUE box, distinct
 *  from the yellow review-comment bubbles so the two never get confused when
 *  both eventually render on the imported document. */
const DeptCommentCell: React.FC<{
    row: number;
    col: number;
    code: string;
    color?: string;
    comment?: string;
    author?: string;
    count?: number;
    selected?: boolean;
    dimmed?: boolean;
    onClick?: (e: React.MouseEvent) => void;
}> = ({ row, col, code, color, comment, author, count, selected, dimmed, onClick }) => (
    <Box
        style={{ gridRow: row, gridColumn: col, alignSelf: "baseline" }}
        onClick={onClick}
        sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start", gap: 0.5, mx: 0.5, minWidth: 0, cursor: "pointer", opacity: dimmed ? 0.35 : 1 }}
    >
        <BaselineStrut />
        {/* Badge only when the fragment currently carries a stamp. A comment can
            outlive its stamp (removed or reassigned), so the box may be badgeless. */}
        {code && (
            <Box
                sx={{
                    width: 28, height: 28, borderRadius: 0.75, flexShrink: 0,
                    bgcolor: color || "primary.main", color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 10, fontWeight: 700,
                }}
            >
                {code}
            </Box>
        )}
        {comment && (
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    px: 0.5, py: 0.25,
                    borderRadius: 1,
                    border: selected ? "1px solid var(--blue-600)" : "1px solid var(--blue-200)",
                    bgcolor: selected ? "var(--blue-200)" : "var(--blue-100)",
                    fontSize: ".75rem",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                }}
            >
                {(author || (count && count > 1)) && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 0.5, mb: 0.25 }}>
                        {author && <Box sx={{ fontWeight: 700, color: "var(--blue-800)" }}>{author}</Box>}
                        {count && count > 1 && (
                            <Box sx={{ color: "var(--blue-600)", fontWeight: 700 }}>{count}</Box>
                        )}
                    </Box>
                )}
                {comment}
            </Box>
        )}
    </Box>
);

export const StaticDocument: React.FC<Props> = ({
    plan,
    gutterWidth = 200,
    deptGutterWidth = 44,
    docLinkGutterWidth = 44,
    aiGutterWidth = 0,
    departments = {},
    documentTypes = {},
    selectedKeys,
    onSelect,
    gridRef,
    filteredKeys,
    hiddenBlocks,
    deptComments,
    commentAuthors,
    suggestionCounts,
    onOpenSuggestions,
    dimmedKeys,
    debugGrid,
    rowConnectors,
    advanced,
}) => {
    // Advanced mode adds a leading master-grid track holding one delete/restore
    // control per top-level block, which pushes every other column right by one.
    const blockToolbar = !!advanced?.onToggleBlock;
    const colOffset = blockToolbar ? 1 : 0;
    // Hover lives here rather than in the container: nothing outside the grid
    // needs it, and keeping it local avoids a container re-render per mouse move.
    const [hoverKey, setHoverKey] = React.useState<string | null>(null);

    // Every annotatable key's absolute master-grid row, reconstructed from the
    // placement plan (no DOM measurement): a subgrid child's row is relative to
    // its parent's, so accumulate the base as we descend. Lets the dept column
    // anchor a comment to a fragment even when that fragment has no dept stamp.
    const keyRow = React.useMemo(() => {
        const out = new Map<string, number>();
        const walk = (blocks: PlacedBlock[], parentBase: number) => {
            for (const b of blocks) {
                const absRow = parentBase + b.rowStart - 1;
                const own = blockKeyOf(b.node);
                if (own && !out.has(own)) out.set(own, absRow);
                if (b.rowKey && !out.has(b.rowKey)) out.set(b.rowKey, absRow);
                if (b.subgrid) walk(b.subgrid.children, absRow);
            }
        };
        walk(plan.content, 1);
        return out;
    }, [plan]);

    // The dept column shows a cell for every stamped row AND — when comment
    // summaries are supplied — every fragment that has a department comment, so an
    // assessor's comment stays visible after its stamp is removed or reassigned.
    // Stamps win the row (they carry the deptId for the badge); comment-only
    // fragments add a badgeless cell at their own row.
    const deptCellByRow = React.useMemo(() => {
        const map = new Map<number, { blockKey: string; deptId?: number }>();
        for (const row of plan.rows) {
            if (row.dept) map.set(row.row, { blockKey: row.dept.blockKey, deptId: row.dept.deptId });
        }
        if (deptComments) {
            for (const key of Object.keys(deptComments)) {
                const r = keyRow.get(key);
                if (r != null && !map.has(r)) map.set(r, { blockKey: key });
            }
        }
        return map;
    }, [plan, deptComments, keyRow]);

    // AI badges ride the same keyRow reconstruction as the dept column: the
    // segmenter knows nothing about suggestions, so the row is resolved from the
    // plan instead. Walk the DOCUMENT's keys (not the suggestion map's) and match
    // lowercased — the API's targetBlockKey casing differs from the markup's.
    // Deduped by row, first key wins: a <tr> key and one of its cell keys can
    // resolve to the same row, and two badges would stack in one cell.
    const aiCellByRow = React.useMemo(() => {
        const map = new Map<number, { blockKey: string; count: number }>();
        if (!suggestionCounts) return map;
        for (const [key, row] of keyRow) {
            const count = suggestionCounts[key.toLowerCase()];
            if (count && !map.has(row)) map.set(row, { blockKey: key, count });
        }
        return map;
    }, [suggestionCounts, keyRow]);

    const DIM_OPACITY = 0.35;
    // One combined attribute-selector rule dims every irrelevant unit wherever
    // its key lives — wrapper, nested element inside rendered innerHTML, or a
    // table cell. Same approach as the selection highlight rule below.
    const dimSelector =
        dimmedKeys && dimmedKeys.size > 0
            ? [...dimmedKeys]
                  .map((k) => `& [data-block-key="${CSS.escape(k)}"]:not([data-subgrid])`)
                  .join(", ")
            : null;
    // Same trick for the selection: one comma-joined rule covers every selected
    // unit, so multi-select costs nothing over single-select.
    const selectedSelector =
        selectedKeys && selectedKeys.size > 0
            ? [...selectedKeys]
                  // Row cells are excluded — a selected row is highlighted by a
                  // single band overlay (see ContentBlock), not per-cell rings.
                  .map((k) => `& [data-block-key="${CSS.escape(k)}"]:not([data-row-cell])`)
                  .join(", ")
            : null;
    const sx: SxProps<Theme> = {
        display: "grid",
        gridTemplateColumns:
            `${blockToolbar ? `${TOOLBAR_TRACK}px ` : ""}` +
            `minmax(0, 1fr) ${gutterWidth}px ${deptGutterWidth}px ${docLinkGutterWidth}px ${aiGutterWidth}px`,
        gridTemplateRows: `repeat(${plan.rowCount}, min-content)`,
        columnGap: 1,
        alignItems: "start",
        ...FIRST_LINE_SX,
        "& table, & ul, & ol": { margin: 0, padding: 0 },
        // The imported document is read-only — you annotate it, you don't edit it.
        // Word source often carries interactive form controls (checkboxes, radios,
        // text fields) that render live via dangerouslySetInnerHTML; neutralize
        // them so a click can't toggle a box that was fixed in the original doc.
        // The gutter/side columns use MUI components (not bare inputs), so this
        // only ever hits document content.
        "& input, & textarea, & select": { pointerEvents: "none" },
        // Paint the search matches. registerHighlight() only registers the ranges;
        // without a ::highlight() rule the matches are invisible (the bug that
        // made search look broken in the static view). Mirrors the classic view.
        "& ::highlight(search-result)": {
            bgcolor: "var(--yellow-400)",
            textDecoration: "underline",
        },
        // Hover feedback, before the selection rules so selection wins on the
        // block you're both hovering and have selected (equal specificity — source
        // order decides). Subgrid containers are skipped: tinting a whole table
        // because the cursor is over one cell would be noise. Row cells get the
        // tint but no ring — hovering one cell tints the whole row (they share the
        // row key), and ringing each cell individually looks broken.
        ...(hoverKey && {
            [`& [data-block-key="${CSS.escape(hoverKey)}"]:not([data-subgrid])`]: {
                backgroundColor: HOVER_TINT,
            },
            [`& [data-block-key="${CSS.escape(hoverKey)}"]:not([data-subgrid]):not([data-row-cell])`]: {
                outline: "1px solid",
                outlineColor: "primary.main",
            },
        }),
        // Highlight the selected units wherever their keys live — wrapper, a
        // nested element inside rendered innerHTML, or every cell of a selected
        // row (cells carry the row key). One rule covers all cases, no measurement.
        ...(selectedSelector && { [selectedSelector]: SELECTED_SX }),
        ...(dimSelector && { [dimSelector]: { opacity: DIM_OPACITY } }),
        // Advanced mode: parts marked for deletion are dimmed in place rather
        // than removed, so the reviewer can see what they are about to lose and
        // undo it. The toolbar controls themselves must stay legible, hence the
        // attribute (set on content only) rather than a positional rule.
        [HIDDEN_PART_SELECTOR]: {
            opacity: 0.4,
            backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
        ...(debugGrid ? (DEBUG_GRID_SX as object) : {}),
    };

    // Hiding a top-level block (search showing only matches) must drop its whole
    // row band, not just the content cell — otherwise its gutter bubble would be
    // orphaned (content + gutter are grid siblings). Collapse the block's rows.
    const hiddenRows = new Set<number>();
    if (hiddenBlocks && hiddenBlocks.size > 0) {
        plan.content.forEach((block, i) => {
            if (!hiddenBlocks.has(i)) return;
            for (let r = block.rowStart; r < block.rowStart + block.rowSpan; r++) hiddenRows.add(r);
        });
    }

    // Click-to-select: resolve the nearest annotated unit from the click target.
    // Gutter bubbles have no data-block-key ancestor, so this no-ops for them
    // (they fire their own onSelect), avoiding double handling.
    const handleClick = (e: React.MouseEvent) => {
        if (!onSelect) return;
        const el = (e.target as HTMLElement).closest("[data-block-key]") as HTMLElement | null;
        const key = el?.getAttribute("data-block-key");
        if (key) onSelect(key, e.ctrlKey || e.metaKey);
    };

    // onMouseOver (not onMouseMove): it bubbles and fires once per element entered,
    // so hover costs one re-render per block crossed rather than one per pixel.
    // Resolves to null over the gutter, which clears the highlight — correct, the
    // gutter is not part of the document.
    const handleMouseOver = (e: React.MouseEvent) => {
        const el = (e.target as HTMLElement).closest("[data-block-key]") as HTMLElement | null;
        setHoverKey(el?.getAttribute("data-block-key") ?? null);
    };

    return (
        <Box
            ref={gridRef}
            className="imported-document"
            sx={sx}
            data-testid="static-document"
            onClick={handleClick}
            onMouseOver={handleMouseOver}
            onMouseLeave={() => setHoverKey(null)}
        >
            {/* Rendered before the content so the hairline paints behind it. */}
            {rowConnectors &&
                plan.rows.map((row) =>
                    row.gutter.kind === "none" || hiddenRows.has(row.row) ? null : (
                        <RowConnector key={`rc${row.row}`} row={row.row} />
                    ),
                )}

            {plan.content.map((block, i) =>
                hiddenBlocks?.has(i) ? null : (
                    <ContentBlock
                        key={`c${i}`}
                        block={block}
                        top
                        selectedKeys={selectedKeys}
                        advanced={advanced}
                        colOffset={colOffset}
                    />
                ),
            )}

            {/* One block control per top-level block, on the leading track and
                spanning the block's whole band. Blocks hidden by search/filter
                are skipped — their rows are collapsed, so a control there would
                have nothing to sit on. */}
            {blockToolbar &&
                plan.content.map((block, i) =>
                    hiddenBlocks?.has(i) || !block.blockId ? null : (
                        <PartControl
                            key={`bt${i}`}
                            hidden={!!advanced?.hiddenBlockIds?.has(block.blockId)}
                            label={blockLabel(block)}
                            onToggle={() => advanced!.onToggleBlock!(block.blockId!)}
                            alignTop
                            style={{
                                gridRow: `${block.rowStart} / span ${block.rowSpan}`,
                                gridColumn: 1,
                            }}
                        />
                    ),
                )}

            {plan.rows.map((row) =>
                hiddenRows.has(row.row) ? null : (
                    <GutterCellView
                        key={`g${row.row}`}
                        row={row}
                        col={2 + colOffset}
                        selectedKeys={selectedKeys}
                        onSelect={onSelect}
                        filteredKeys={filteredKeys}
                        commentAuthors={commentAuthors}
                    />
                ),
            )}

            {[...deptCellByRow.entries()].map(([rowNum, info]) => {
                if (hiddenRows.has(rowNum)) return null;
                const dept = info.deptId != null ? departments[info.deptId] : undefined;
                const deptDimmed = !!dimmedKeys && dimmedKeys.has(info.blockKey);
                // Assess/edit mode: badge (when stamped) + comment bubble in the
                // widened dept column. The comment shows whether or not the
                // fragment currently has a stamp.
                if (deptComments) {
                    const dc = deptComments[info.blockKey];
                    if (!dept && !dc) return null; // nothing to show on this row
                    return (
                        <DeptCommentCell
                            key={`d${rowNum}`}
                            row={rowNum}
                            col={3 + colOffset}
                            code={dept?.code ?? ""}
                            color={dept?.color}
                            comment={dc?.comment}
                            author={dc?.authorFullName}
                            count={dc?.count}
                            selected={!!selectedKeys?.has(info.blockKey)}
                            dimmed={deptDimmed}
                            onClick={(e) => onSelect?.(info.blockKey, e.ctrlKey || e.metaKey)}
                        />
                    );
                }
                // Badge-only mode (no comment summaries): render just for stamps.
                if (!dept) return null;
                return (
                    <SideChip
                        key={`d${rowNum}`}
                        row={rowNum}
                        col={3 + colOffset}
                        code={dept.code}
                        color={dept.color}
                        dimmed={deptDimmed}
                        onClick={(e) => onSelect?.(info.blockKey, e.ctrlKey || e.metaKey)}
                    />
                );
            })}

            {plan.rows.map((row) =>
                !hiddenRows.has(row.row) && row.docLink && documentTypes[row.docLink.docLinkId] ? (
                    <SideChip
                        key={`l${row.row}`}
                        row={row.row}
                        col={4 + colOffset}
                        code={documentTypes[row.docLink.docLinkId].code}
                        color={documentTypes[row.docLink.docLinkId].color}
                        onClick={(e) => onSelect?.(row.docLink!.blockKey, e.ctrlKey || e.metaKey)}
                    />
                ) : null,
            )}

            {[...aiCellByRow.entries()].map(([rowNum, info]) =>
                hiddenRows.has(rowNum) ? null : (
                    <AiSuggestionChip
                        key={`ai${rowNum}`}
                        row={rowNum}
                        col={5 + colOffset}
                        count={info.count}
                        onClick={(e) => {
                            const multi = e.ctrlKey || e.metaKey;
                            onSelect?.(info.blockKey, multi);
                            if (!multi) onOpenSuggestions?.(info.blockKey);
                        }}
                    />
                ),
            )}
        </Box>
    );
};

export default StaticDocument;
